import { useState, useEffect } from "react";
import { Dumbbell, Loader2, CheckCircle2 } from "lucide-react";
import { api } from "@/lib/api";
import { ProBadge } from "@/components/AskAI";

const DIFF = {
  Easy: "bg-emerald-50 text-emerald-700 dark:bg-emerald-500/15 dark:text-emerald-300",
  Medium: "bg-amber-50 text-amber-700 dark:bg-amber-500/15 dark:text-amber-300",
  Hard: "bg-rose-50 text-rose-700 dark:bg-rose-500/15 dark:text-rose-300",
};

export function PracticeTasks({ videoId }) {
  const [tasks, setTasks] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let on = true;
    setLoading(true);
    api.get(`/pro/practice/${videoId}`)
      .then(({ data }) => { if (on) setTasks(data.tasks || []); })
      .catch(() => { if (on) setTasks([]); })
      .finally(() => { if (on) setLoading(false); });
    return () => { on = false; };
  }, [videoId]);

  if (!loading && (!tasks || tasks.length === 0)) return null;

  return (
    <div className="bg-card border border-border shadow-sm rounded-2xl p-6 md:p-7" data-testid="practice-tasks">
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-3">
          <span className="grid place-items-center h-9 w-9 rounded-xl bg-primary/10 text-primary"><Dumbbell className="h-4 w-4" /></span>
          <h3 className="font-heading font-bold text-xl">Practice &amp; build</h3>
        </div>
        <ProBadge />
      </div>
      <p className="mt-2 text-sm text-muted-foreground">Lock it in with hands-on tasks (10–30 min each) — and make something real.</p>

      {loading ? (
        <div className="mt-4 flex items-center gap-2 text-muted-foreground text-sm">
          <Loader2 className="h-4 w-4 animate-spin" /> Generating your practice tasks…
        </div>
      ) : (
        <div className="mt-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-1 xl:grid-cols-3">
          {tasks.map((t, i) => (
            <div key={i} className="rounded-xl border border-border bg-background p-4" data-testid="practice-task">
              <div className="flex items-start justify-between gap-2">
                <h4 className="font-heading font-bold text-sm leading-tight">{t.title}</h4>
                <span className={`shrink-0 rounded-full text-[11px] font-semibold px-2 py-0.5 ${DIFF[t.difficulty] || "bg-secondary text-muted-foreground"}`}>{t.difficulty}</span>
              </div>
              <p className="mt-2 text-sm text-muted-foreground leading-relaxed">{t.task}</p>
              {t.deliverable && (
                <p className="mt-2 text-xs flex items-start gap-1.5 text-foreground/80">
                  <CheckCircle2 className="h-3.5 w-3.5 text-success shrink-0 mt-0.5" /> You'll make: {t.deliverable}
                </p>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
